'use client'

import { useEffect, useState } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import {
  MoreVertical,
  Heart,
  MessageSquare,
  Share2,
  Flag,
  Trash,
  Edit,
} from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useRouter } from 'next/navigation'
import { ProfileUtils } from 'utils/profile'
import {
  Post,
  useAddCommentMutation,
  useBlockUserMutation,
  useDeletePostMutation,
  useFollowerFollowingLazyQuery,
  useFollowUserMutation,
  useGetPostDetailQuery,
  useGetPostLikesQuery,
  useLikePostMutation,
  useListingDetailQuery,
  useLogShareMutation,
  User,
  useUnFollowUserMutation,
} from 'graphql/generated/graphql'
import { buildShareUrl } from '@/lib/sharing'
import { getUserId } from '@/lib/utils'
import { RootState } from 'features/store'
import { useDispatch, useSelector } from 'react-redux'
import { setFollowerFOllowing } from 'features/authSlice'
import { toast } from 'react-toastify'
import { ConfirmDialog } from './confirm-dialog'
import { PostShareModal } from './post/PostShareModal'
import { isUserAvailable } from 'helpers/authUser'
import { useAuth } from '@/lib/authContext'
import { postHasHeroImage } from '@/lib/postImage'

function formatPostTime(dateString?: string | null): string {
  if (!dateString) return 'Just now'
  try {
    const date = new Date(dateString)
    if (isNaN(date.getTime())) return String(dateString)
    const now = new Date()
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000)
    if (diffInSeconds < 60) return 'Just now'
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  } catch {
    return 'Just now'
  }
}

interface Product {
  id: string
  name: string
  image: string
}

interface PostProps {
  post: {
    post_products: any
    post_recipes: any
    post_medias?: { url?: string }[]
    id: number
    user_id: number
    user: User
    visibility: string
    timestamp?: string
    created_at?: string
    updated_at?: string
    content: string
    products?: Product[]
    recipeIdeas?: number
    likes?: number
    likeCount?: number
    hasImage?: boolean
    image?: string
    slug?: string
    title?: string | null
    status: string
    group?: string | null
  }
}

export function FeedPost({ post }: PostProps) {
  const dispatch = useDispatch()
  const { user, followerFollowing } = useSelector((state: RootState) => state.culineerAuth)
  const userAvailable = isUserAvailable(user)
  const { checkAuth } = useAuth()
  const [isLiked, setIsLiked] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [showBlockConfirm, setShowBlockConfirm] = useState(false)
  const [showReportConfirm, setShowReportConfirm] = useState(false)
  const [shareModalOpen, setShareModalOpen] = useState(false)
  const router = useRouter()

  // Queries
  const { data, error, refetch } = useGetPostDetailQuery({
    skip: !post?.slug,
    variables: { slug: post?.slug },
  })
  const postData = data?.getPost ? { ...post, ...data.getPost } : post

  const { data: listing, loading } = useListingDetailQuery({
    fetchPolicy: 'no-cache',
    variables: {
      listingId: postData?.user.default_listing_id,
    },
    skip: !postData?.user?.default_listing_id,
  })

  const { data: likesData, refetch: refetchPostLikes } = useGetPostLikesQuery({
    skip: !post?.id,
    variables: { postId: postData?.id || 0 },
  })

  const [addComment, { loading: addingComment }] = useAddCommentMutation()
  const [commentText, setCommentText] = useState('')
  const [isFollowingAuthor, setIsFollowingAuthor] = useState(false)
  const [followUser] = useFollowUserMutation()
  const [unfollowUser] = useUnFollowUserMutation()
  const [blockUser] = useBlockUserMutation()
  const [getFollowerFollowing] = useFollowerFollowingLazyQuery({ fetchPolicy: 'no-cache' })

  const authorUserId = postData?.user?.user_id
  const authorUserType = postData?.user?.user_type?.toLowerCase()

  // Seed isFollowingAuthor from real data instead of always starting false --
  // otherwise a post from someone you already follow shows "Follow" until you
  // toggle it once this session.
  useEffect(() => {
    if (!followerFollowing && user?.user_id) {
      getFollowerFollowing({ variables: { userId: user.user_id, limit: 1000, offset: 1, searchTerm: '' } })
        .then((res) => {
          // @ts-ignore
          if (res.data) dispatch(setFollowerFOllowing(res.data))
        })
    }
  }, [followerFollowing, user?.user_id])

  useEffect(() => {
    if (authorUserId) {
      setIsFollowingAuthor(
        !!followerFollowing?.MyFollowing?.results?.some((u) => u.user_id === authorUserId)
      )
    }
  }, [followerFollowing, authorUserId])

  const handleFollowAuthor = async () => {
    if (!checkAuth()) return
    if (!authorUserId) return

    const prev = isFollowingAuthor
    setIsFollowingAuthor(!prev)
    try {
      if (prev) {
        await unfollowUser({ variables: { userID: authorUserId } })
        toast.success('Unfollowed user')
      } else {
        await followUser({ variables: { userID: authorUserId } })
        toast.success('User followed')
      }
    } catch {
      setIsFollowingAuthor(prev)
      toast.error('Failed to update follow status')
    }
  }

  const handleUnfollowAuthor = async (e?: React.MouseEvent) => {
    e?.stopPropagation()
    if (!checkAuth()) return
    if (!authorUserId) return

    try {
      await unfollowUser({ variables: { userID: authorUserId } })
      setIsFollowingAuthor(false)
      toast.success('User unfollowed')
    } catch (err: any) {
      toast.error(err?.message || 'Failed to unfollow user')
    }
  }

  const handleBlockAuthor = async () => {
    if (!checkAuth()) return
    if (!authorUserId) return

    try {
      await blockUser({ variables: { userId: authorUserId } })
      setIsFollowingAuthor(false)
      toast.success('User removed and blocked')
    } catch (err: any) {
      toast.error(err?.message || 'Failed to block user')
    } finally {
      setShowBlockConfirm(false)
    }
  }

  const handleReportUser = () => {
    if (!checkAuth()) return
    setShowReportConfirm(false)
    toast.success('Thank you for reporting. Our team will review this user.')
  }

  const handleAddComment = async (e?: React.FormEvent) => {
    if (e) e.preventDefault()
    if (!checkAuth()) return
    if (!commentText.trim() || !postData?.id) return

    try {
      await addComment({
        variables: {
          postId: postData.id,
          content: commentText.trim(),
        },
      })
      setCommentText('')
      toast.success('Comment added')
      refetch()
    } catch {
      toast.error('Failed to add comment')
    }
  }

  const [likePost] = useLikePostMutation()
  const [deletePost] = useDeletePostMutation()
  const [logShare] = useLogShareMutation()

  const currentUserId = getUserId()
  const isPostOwner = currentUserId === postData?.user?.user_id

  useEffect(() => {
    if (postData && likesData) {
      const userLiked = likesData.getPostLikes?.some(
        (like) => like.user_id === currentUserId
      )
      setIsLiked(!!userLiked)
    }
  }, [postData?.id, likesData?.getPostLikes, currentUserId])

  const handleLike = async () => {
    if (!checkAuth()) return
    if (!postData?.id) return

    try {
      await likePost({
        variables: { postId: postData.id },
      })
      refetch()
      refetchPostLikes()
    } catch {
      setIsLiked(!isLiked)
      toast.error('Failed to like post')
    }
  }

  const handlePostClick = () => {
    if (post?.slug) {
      router.refresh()
      router.push(`/post/${post.slug}`)
    }
  }

  const getAuthorUrl = () => {
    const userType = post?.user?.user_type?.toLowerCase()
    const listingSlug = post?.user?.default_listing?.slug
    if ((userType === 'producer' || userType === 'market') && listingSlug) {
      return `/${userType}/${listingSlug}`
    }
    return `/member/${post?.user?.slug || ''}`
  }

  const handleViewProfile = (e?: React.MouseEvent) => {
    e?.stopPropagation()
    router.push(getAuthorUrl())
  }

  const handleEditPost = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!postData?.slug) return
    router.push(`/posts/edit/${postData.slug}`)
  }

  const handleDeletePost = async () => {
    if (!postData?.id) return

    try {
      await deletePost({
        variables: { postID: postData.id },
      })
      toast.success('Post deleted successfully')
      router.back()
    } catch {
      toast.error('Failed to delete post')
    } finally {
      setShowDeleteConfirm(false)
    }
  }

  const renderPostMenu = () => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {userAvailable && (
          <button
            type="button"
            onClick={(e) => e.stopPropagation()}
            className="rounded-full p-2 hover:bg-gray-100 text-gray-500"
          >
            <MoreVertical className="h-5 w-5" />
          </button>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-44 bg-white" align="end">
        {isPostOwner ? (
          <>
            <DropdownMenuItem
              onClick={handleEditPost}
              className="flex cursor-pointer items-center gap-2"
            >
              <Edit className="h-4 w-4" />
              <span>Edit Post</span>
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation()
                setShowDeleteConfirm(true)
              }}
              className="flex cursor-pointer items-center gap-2 text-red-500"
            >
              <Trash className="h-4 w-4" />
              <span>Delete Post</span>
            </DropdownMenuItem>
          </>
        ) : (
          <div className='space-y-1'>
            <DropdownMenuItem
              className="flex cursor-pointer items-center gap-2 text-primary bg-[#F3FAEB]"
              onClick={handleViewProfile}
            >
              <span className='py-1'>View Profile</span>
            </DropdownMenuItem>
            {isFollowingAuthor && (
              <DropdownMenuItem
                className="flex cursor-pointer items-center gap-2 text-[#FF3B30] bg-[#FF3B301A]"
                onClick={handleUnfollowAuthor}
              >
                <span className='py-1'>Unfollow</span>
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              className="flex cursor-pointer items-center gap-2 text-gray-700 bg-[#F7F7F7]"
              onClick={(e) => {
                e.stopPropagation()
                setShowBlockConfirm(true)
              }}
            >
              <span className='py-1'>Remove {'&'} Block</span>
            </DropdownMenuItem>
            <DropdownMenuItem
              className="flex cursor-pointer items-center gap-2 text-red-500"
              onClick={(e) => {
                e.stopPropagation()
                setShowReportConfirm(true)
              }}
            >
              <Flag className="h-4 w-4" />
              <span className='py-1'>Report User</span>
            </DropdownMenuItem>
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )

  const viewerUserType = (user?.user_type || '').toLowerCase()
  const userAvatarSrc =
    viewerUserType === 'producer'
      ? (user as any)?.default_listing?.photo || '/assets/icons/tractor.svg'
      : viewerUserType === 'market'
        ? (user as any)?.default_listing?.photo || '/assets/icons/Market.svg'
        : user?.profile_photo_resource || '/assets/avatar/user-default.svg'

  const getExcerpt = (html?: string | null) => {
    if (!html) return ''
    return html
      .replace(/<[^>]+>/g, ' ')
      .replace(/&nbsp;/gi, ' ')
      .replace(/&amp;/gi, '&')
      .replace(/&lt;/gi, '<')
      .replace(/&gt;/gi, '>')
      .replace(/&quot;/gi, '"')
      .replace(/&#39;|&apos;/gi, "'")
      .replace(/&rsquo;|&rdquo;|&lsquo;|&ldquo;/gi, "'")
      .replace(/&mdash;|&ndash;/gi, '-')
      .replace(/\s+/g, ' ')
      .trim()
  }

  const previewText = getExcerpt(postData?.content || post?.content)
  const heroImage =
    post?.post_medias?.[0]?.url ||
    (postData as any)?.post_medias?.[0]?.url ||
    (post?.hasImage && post?.image ? post.image : null)
  const isBlogPost =
    post?.group === 'admin' || (postData as any)?.group === 'admin'
  const showPublishDate = !isBlogPost
  const postProducts = post?.post_products || (postData as any)?.post_products || []
  const recipeCount =
    post?.post_recipes?.length ||
    (postData as any)?.post_recipes?.length ||
    (post as any)?.recipeIdeas ||
    0
  const isBusinessAuthor = authorUserType === 'producer' || authorUserType === 'market'
  const authorBadgeLabel =
    authorUserType === 'market'
      ? 'Market'
      : authorUserType === 'producer'
        ? 'Producer'
        : 'Member'
  const authorName = ProfileUtils.getName(
    postData?.user,
    listing?.listingDetail as any
  )
  const authorImage =
    ProfileUtils.getImage(postData?.user, listing?.listingDetail as any) ||
    '/assets/avatar/user-default.svg'
  const likeActive = !!(isLiked || postData?.likeCount || postData?.likes)

  const renderActionRow = () => (
    <div className="flex items-start gap-4">
      <button
        type="button"
        className={`flex h-[45px] flex-1 items-center justify-center gap-2.5 rounded-full px-2 py-3 text-sm font-medium leading-[150%] transition ${
          likeActive
            ? 'bg-[#85C349] text-white hover:bg-[#75B339]'
            : 'bg-[#F7F7F7] text-[#202020] hover:bg-gray-200'
        }`}
        onClick={(e) => {
          e.stopPropagation()
          handleLike()
        }}
      >
        <Heart
          className={`h-[21px] w-[21px] ${
            likeActive ? 'fill-white text-white' : 'text-[#202020]'
          }`}
        />
        <span>Like ({postData?.likeCount || postData?.likes || 0})</span>
      </button>

      <button
        type="button"
        className="flex h-[45px] flex-1 items-center justify-center gap-2.5 rounded-full bg-[#F7F7F7] px-2 py-3 text-sm font-medium leading-[150%] text-[#202020] transition hover:bg-gray-200"
        onClick={(e) => {
          e.stopPropagation()
          handlePostClick()
        }}
      >
        <MessageSquare className="h-[21px] w-[21px] text-[#202020]" />
        <span>Comment</span>
      </button>

      <button
        type="button"
        className="flex h-[45px] flex-1 items-center justify-center gap-2.5 rounded-full bg-[#F7F7F7] px-2 py-3 text-sm font-medium leading-[150%] text-[#202020] transition hover:bg-gray-200"
        onClick={(e) => {
          e.stopPropagation()
          setShareModalOpen(true)
        }}
      >
        <Share2 className="h-[21px] w-[21px] text-[#202020]" />
        <span>Share</span>
      </button>
    </div>
  )

  // Blog / admin posts: clean article card (image, title, publisher, 2-line preview)
  if (isBlogPost) {
    return (
      <div className="mb-4 cursor-pointer overflow-hidden rounded-xl bg-white p-2 sm:mb-5">
        <div className="mb-3 flex items-center justify-between gap-3">
          <div
            className="flex min-w-0 flex-1 items-center gap-3 cursor-pointer"
            onClick={(e) => {
              e.stopPropagation()
              router.push(getAuthorUrl())
            }}
          >
            <Image
              src={authorImage}
              alt={authorName}
              width={40}
              height={40}
              unoptimized
              className="h-10 w-10 flex-shrink-0 rounded-full object-cover"
            />
            <h4 className="truncate text-sm font-medium text-[#202020] hover:underline sm:text-base">
              {authorName}
            </h4>
          </div>
          {renderPostMenu()}
        </div>

        <div className="relative flex flex-col gap-3" onClick={handlePostClick}>
          {heroImage && (
            <div className="overflow-hidden rounded-xl">
              <div className="relative aspect-video max-h-[394px] w-full bg-[#F7F7F7]">
                <Image
                  src={heroImage}
                  alt={postData?.title || 'Blog image'}
                  fill
                  sizes="(max-width: 1440px) calc(100vw - 64px), 721px"
                  className="object-cover"
                  priority
                />
              </div>
            </div>
          )}

          {postData?.title && (
            <h3 className="line-clamp-2 text-base font-bold text-[#0D1000] sm:text-lg">
              {postData.title}
            </h3>
          )}

          {previewText && (
            <p className="line-clamp-2 text-sm leading-relaxed text-[#6B6B6B]">
              {previewText}
            </p>
          )}

          {renderActionRow()}
        </div>

        <ConfirmDialog
          open={showDeleteConfirm}
          onOpenChange={setShowDeleteConfirm}
          title="Delete Post"
          description="Are you sure you want to delete this post? This action cannot be undone."
          confirmText="Delete"
          cancelText="Cancel"
          onConfirm={handleDeletePost}
          variant="destructive"
        />
        <ConfirmDialog
          open={showBlockConfirm}
          onOpenChange={setShowBlockConfirm}
          title="Block User"
          description="Are you sure you want to remove and block this user? You will no longer see their posts or profile."
          confirmText="Remove & Block"
          cancelText="Cancel"
          onConfirm={handleBlockAuthor}
          variant="destructive"
        />
        <ConfirmDialog
          open={showReportConfirm}
          onOpenChange={setShowReportConfirm}
          title="Report User"
          description="Are you sure you want to report this user for violating community guidelines?"
          confirmText="Report User"
          cancelText="Cancel"
          onConfirm={handleReportUser}
          variant="destructive"
        />
        <PostShareModal
          open={shareModalOpen}
          onClose={() => setShareModalOpen(false)}
          post={{
            slug: postData?.slug || '',
            id: postData?.id,
            title: postData?.title,
            content: postData?.content,
            group: postData?.group,
          }}
          userSlug={user?.slug}
          hasPhoto={postHasHeroImage(postData)}
          isOwner={isPostOwner}
        />
      </div>
    )
  }

  // Community feed posts (general / producer / market)
  return (
    <div className="mb-4 cursor-pointer overflow-hidden rounded-xl bg-white p-2 sm:mb-5">
      <div className="mb-3 flex items-center justify-between gap-4">
        <div
          className="flex min-w-0 flex-1 cursor-pointer items-center gap-3"
          onClick={(e) => {
            e.stopPropagation()
            router.push(getAuthorUrl())
          }}
        >
          <Image
            src={authorImage}
            alt={authorName}
            width={44}
            height={44}
            unoptimized
            className="h-11 w-11 flex-shrink-0 rounded-full object-cover"
          />
          <div className="flex min-w-0 flex-col gap-1.5">
            <h4 className="truncate text-base font-medium leading-[19px] text-[#202020] hover:underline">
              {authorName}
            </h4>
            {isBusinessAuthor ? (
              <span className="inline-flex w-fit items-center rounded-full bg-[#DFEDFA] px-2 py-0.5 text-sm font-medium leading-6 text-[#2D51A0]">
                {authorBadgeLabel}
              </span>
            ) : (
              <span className="text-base font-normal leading-[19px] text-[#6B6B6B]">
                Member
              </span>
            )}
          </div>

          {isBusinessAuthor && !isPostOwner && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                handleFollowAuthor()
              }}
              className={`ml-auto flex-shrink-0 rounded-full px-4 py-2 text-base font-medium transition ${
                isFollowingAuthor
                  ? 'bg-[#E5F3D4] text-[#548828]'
                  : 'bg-[#85C349] text-white shadow-[0px_8px_16px_rgba(0,0,0,0.04)] hover:bg-[#75B339]'
              }`}
            >
              {isFollowingAuthor ? 'Following' : 'Follow'}
            </button>
          )}

          {user?.user_id === post?.user_id && (
            <span
              className={`flex-shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium text-white ${
                post?.status?.toLowerCase() === 'published'
                  ? 'bg-[#3B82F6]'
                  : 'bg-gray-400'
              }`}
            >
              {post?.status?.toLowerCase() === 'published' ? 'Published' : 'Draft'}
            </span>
          )}
        </div>

        <div className="flex flex-shrink-0 items-center gap-4">
          {showPublishDate && (
            <span className="text-base font-medium leading-[19px] tracking-[-0.05em] text-[#6B6B6B]">
              {formatPostTime(
                (postData as any)?.created_at ||
                  (postData as any)?.updated_at ||
                  (postData as any)?.timestamp
              )}
            </span>
          )}
          {renderPostMenu()}
        </div>
      </div>

      <div className="relative flex flex-col gap-3" onClick={handlePostClick}>
        {/* Image above text when present */}
        {heroImage && (
          <div className="overflow-hidden rounded-xl">
            <div className="relative aspect-video max-h-[325px] w-full bg-[#F7F7F7]">
              <Image
                src={heroImage}
                alt={postData?.title || 'Post image'}
                fill
                sizes="(max-width: 1440px) calc(100vw - 64px), 721px"
                className="object-cover"
                priority
              />
            </div>
          </div>
        )}

        {previewText && (
          <p className="text-base font-normal leading-[150%] text-[#0D1000] line-clamp-2">
            {previewText}
          </p>
        )}

        {postProducts.length > 0 && (
          <div className="grid w-full grid-cols-2 gap-3 sm:grid-cols-4">
            {postProducts.slice(0, 3).map(({ product }: any) => (
              <div
                key={product.ingredient_id || product.id}
                className="flex min-h-[162px] flex-col items-center rounded-lg border border-[#E6E6E6] bg-white px-2 py-1.5 text-center"
              >
                <div className="relative mb-1.5 flex h-[112px] w-[112px] items-center justify-center">
                  <Image
                    src={
                      product.illustrationImage ||
                      product.mainImage ||
                      '/placeholder.svg'
                    }
                    alt={product.name}
                    width={112}
                    height={112}
                    unoptimized
                    className="max-h-[112px] max-w-[112px] object-contain"
                  />
                </div>
                <span className="line-clamp-2 text-center text-sm font-bold leading-4 text-[#0D1000]">
                  {product.name}
                </span>
              </div>
            ))}

            {postProducts.length > 3 && (
              <div className="flex min-h-[162px] flex-col items-center justify-center rounded-lg bg-[#F3FAEB] px-2 py-2 text-center">
                <span className="text-center text-base font-semibold leading-[19px] text-[#85C349]">
                  View all {postProducts.length} Products
                </span>
              </div>
            )}
          </div>
        )}

        {!!recipeCount && (
          <div className="flex items-center justify-center rounded-xl bg-[#F1F7FD] px-4 py-4">
            <span className="text-base font-semibold leading-[19px] text-[#3978D7]">
              {recipeCount} Recipe Ideas
            </span>
          </div>
        )}

        {renderActionRow()}

        <div
          className="flex h-[50px] items-center gap-2.5 rounded-full border border-[#CBD7DD] bg-[#F7F7F7] p-2"
          onClick={(e) => e.stopPropagation()}
        >
          <Image
            src={userAvatarSrc}
            alt="User avatar"
            width={34}
            height={34}
            unoptimized
            className="h-[34px] w-[34px] flex-shrink-0 rounded-full object-cover bg-gray-100"
          />
          <input
            type="text"
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleAddComment()
            }}
            placeholder="Write comment..."
            className="min-w-0 flex-1 border-none bg-transparent text-sm font-normal leading-[17px] tracking-[-0.05em] text-[#202020] placeholder-[#6B6B6B] focus:outline-none"
          />
          <button
            type="button"
            onClick={() => handleAddComment()}
            disabled={!commentText.trim() || addingComment}
            className="h-[34px] flex-shrink-0 rounded-full bg-[#85C349] px-4 text-sm font-medium tracking-[-0.05em] text-white shadow-[0px_8px_16px_rgba(0,0,0,0.04)] transition hover:bg-[#75B339] disabled:opacity-50"
          >
            {addingComment ? '...' : 'Send'}
          </button>
        </div>
      </div>

      {/* Delete confirmation dialog */}
      <ConfirmDialog
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        title="Delete Post"
        description="Are you sure you want to delete this post? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleDeletePost}
        variant="destructive"
      />

      {/* Block confirmation dialog */}
      <ConfirmDialog
        open={showBlockConfirm}
        onOpenChange={setShowBlockConfirm}
        title="Block User"
        description="Are you sure you want to remove and block this user? You will no longer see their posts or profile."
        confirmText="Remove & Block"
        cancelText="Cancel"
        onConfirm={handleBlockAuthor}
        variant="destructive"
      />

      {/* Report confirmation dialog */}
      <ConfirmDialog
        open={showReportConfirm}
        onOpenChange={setShowReportConfirm}
        title="Report User"
        description="Are you sure you want to report this user for violating community guidelines?"
        confirmText="Report User"
        cancelText="Cancel"
        onConfirm={handleReportUser}
        variant="destructive"
      />

      <PostShareModal
        open={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
        post={{
          slug: postData?.slug || '',
          id: postData?.id,
          title: postData?.title,
          content: postData?.content,
          group: postData?.group,
        }}
        userSlug={user?.slug}
        hasPhoto={postHasHeroImage(postData)}
        isOwner={isPostOwner}
      />
    </div>
  )
}
